import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TechnicalSupportChatbot from '../components/TechnicalSupportChatbot';
import ConnectionStatus from '../components/ConnectionStatus';
import { useChatApi } from '../contexts/ChatApiContext';

const AlexChatPage: React.FC = () => {
    const { conversationId } = useParams<{ conversationId?: string }>();
    const navigate = useNavigate();
    const { isConnected, isConnecting, connect } = useChatApi();
    const [showConnectionModal, setShowConnectionModal] = useState<boolean>(!isConnected);
    const [connectionAttempted, setConnectionAttempted] = useState<boolean>(false);

    const numericConversationId = conversationId
        ? parseInt(conversationId, 10)
        : undefined;

    useEffect(() => {
        if (!isConnected && !connectionAttempted && !isConnecting) {
            console.log('AlexChatPage: Auto-connecting with default parameters');
            connect();
            setConnectionAttempted(true);
        }
    }, [isConnected, connectionAttempted, isConnecting, connect]);

    useEffect(() => {
        console.log('AlexChatPage: Connection status from context:', isConnected, 'connecting:', isConnecting);
        setShowConnectionModal(!isConnected || isConnecting);
    }, [isConnected, isConnecting]);

    return (
        <div className="flex flex-col h-screen relative">
            {showConnectionModal && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold mb-4">Connection Required</h2>

                        {isConnecting ? (
                            <div className="mb-4">
                                <p className="mb-2">Establishing connection to the AI service...</p>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div className="bg-blue-600 h-2.5 rounded-full animate-pulse w-full"></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">This may take a few seconds...</p>
                            </div>
                        ) : connectionAttempted && !isConnected ? (
                            <div className="mb-4">
                                <p className="mb-2">Connection failed. Please try again.</p>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div className="bg-red-600 h-2.5 rounded-full w-full"></div>
                                </div>
                            </div>
                        ) : (
                            <p className="mb-4">You need to connect to the AI service before using the chat.</p>
                        )}

                        <ConnectionStatus redirectOnConnect={false} />

                        <div className="flex justify-end mt-4">
                            <button
                                onClick={() => navigate('/')}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 mr-2"
                            >
                                Back to Home
                            </button>
                            {isConnected && (
                                <button
                                    onClick={() => setShowConnectionModal(false)}
                                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                    Continue to Chat
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-hidden">
                <TechnicalSupportChatbot
                    initialConversationId={numericConversationId}
                    forceConnected={isConnected}
                />
            </div>
        </div>
    );
};

export default AlexChatPage;